import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const Row = ({ label, value, strong = false }) => (
  <div className={`flex items-baseline justify-between gap-6 py-2 ${strong ? "" : "border-b border-[#d8d4c8]"}`}>
    <span className={`${strong ? "font-semibold" : "text-[#5b564a]"} text-[13px]`}>{label}</span>
    <span className={`${strong ? "font-semibold text-lg" : ""} text-right`}>{value}</span>
  </div>
);

const Receipt = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState("loading");
  const [doc, setDoc] = useState(null);
  const [paying, setPaying] = useState(false);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    fetch(`${API}/api/receipts/${id}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error();
        setDoc(await res.json());
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [id]);

  useEffect(() => {
    if (!sessionId || !id) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          const fresh = await fetch(`${API}/api/receipts/${id}`, { credentials: "include" });
          if (fresh.ok) setDoc(await fresh.json());
          return;
        }
      } catch {
        // keep polling
      }
      if (tries < 20) timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, id]);

  const pay = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ receipt_id: id, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } finally {
      setPaying(false);
    }
  };

  if (state === "loading") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink">
        <MoonMark className="h-12 w-12 animate-spin text-gold" style={{ animationDuration: "6s" }} />
        <p className="font-jmono text-[10px] uppercase tracking-[0.4em] text-slate-500">
          Unsealing the receipt…
        </p>
      </main>
    );
  }

  if (state === "error" || !doc) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-8 px-6 pb-24 pt-28 text-center">
        <MoonMark className="h-12 w-12 text-gold" />
        <h1 className="font-display text-2xl tracking-[0.15em] text-silver">NO RECEIPT FOUND</h1>
        <p className="max-w-sm text-sm font-light text-slate-400">
          This receipt does not exist, or your session does not have the right to read it.
        </p>
        <Link to="/account" data-testid="receipt-back-account" className="btn-night">
          Back to My Quotes
        </Link>
      </main>
    );
  }

  const q = doc.quote || {};
  const r = doc.receipt;
  const details = [
    ["Scale", q.scale],
    ["Deadline", q.deadline],
    ["Revisions", q.revisions],
    ["Licensing", q.licensing],
    ["Rush fee", q.rush ? "Yes (+20%)" : "No"],
  ].filter(([, v]) => v);

  return (
    <main className="mx-auto max-w-2xl px-6 pb-32 pt-32">
        <div className="no-print mb-8 flex items-center justify-between">
          <Link to="/account" data-testid="receipt-link-account" className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-gold">
            ← My Quotes
          </Link>
          <div className="flex items-center gap-3">
            {doc.receipt.paid ? (
              <span className="border border-emerald-500/60 px-4 py-2 font-jmono text-[9px] uppercase tracking-[0.3em] text-emerald-400" data-testid="receipt-paid-chip">
                Paid
              </span>
            ) : (
              <button onClick={pay} disabled={paying} data-testid="receipt-pay-btn" className="btn-night !px-5 !py-2">
                {paying ? "Opening Stripe…" : `Pay ${fmtUSD(doc.receipt.amount)}`}
              </button>
            )}
            <button onClick={() => window.print()} data-testid="receipt-print-btn" className="btn-ghost-night !px-5 !py-2">
              Print
            </button>
          </div>
        </div>

      <article
        className="print-sheet relative bg-[#f8f6f0] p-8 text-[#1c1a16] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)] md:p-12"
        data-testid="receipt-sheet"
      >
        <div className="flex items-start justify-between gap-6 border-b-2 border-[#1c1a16] pb-6">
          <div>
            <div className="flex items-center gap-3">
              <MoonMark className="h-9 w-9 text-[#7c3aed]" />
              <span className="font-display text-xl tracking-[0.2em]">NOX HARMONIA</span>
            </div>
            <p className="mt-2 text-[11px] uppercase tracking-[0.25em] text-[#5b564a]">
              Paul Elliott Messier · Harmonies of the Night
            </p>
          </div>
          <div className="text-right">
            <p className="font-display text-2xl tracking-[0.15em]">RECEIPT</p>
            <p className="mt-1 font-jmono text-[11px] tracking-[0.15em] text-[#5b564a]" data-testid="receipt-number">
              {r.number}
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#5b564a]">Billed to</p>
            <p className="mt-1 text-sm font-medium">{doc.name}</p>
            <p className="text-sm text-[#5b564a]">{doc.email}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#5b564a]">Issued</p>
            <p className="mt-1 text-sm">{r.issued_at ? new Date(r.issued_at).toLocaleDateString() : ""}</p>
          </div>
        </div>

        <div className="mt-8">
          <Row label="Description" value={q.service || "Commission — original score & direction"} strong />
          {details.map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
        </div>

        <div className="mt-6 border-t-2 border-[#1c1a16] pt-4">
          <Row label="TOTAL" value={fmtUSD(r.amount)} strong />
          <p className="mt-1 text-right font-jmono text-[10px] uppercase tracking-[0.25em] text-[#5b564a]">
            {r.currency} · all services rendered under this commission
          </p>
        </div>

        <div className="mt-10 flex items-center justify-between border-t border-[#d8d4c8] pt-6">
          <p className="max-w-xs text-[10px] leading-relaxed text-[#5b564a]">
            Issued for tax purposes by Nox Harmonia (Paul Elliott Messier). This receipt reflects
            the commissioned score and creative services described above.
          </p>
          <div className="flex items-center gap-4">
            {doc.receipt.paid && (
              <div className="border-2 border-emerald-600 px-4 py-2 text-center" data-testid="receipt-paid-stamp">
                <p className="font-display text-lg tracking-[0.2em] text-emerald-600">PAID</p>
                <p className="font-jmono text-[8px] uppercase tracking-[0.3em] text-emerald-600">
                  {doc.receipt.paid_at ? new Date(doc.receipt.paid_at).toLocaleDateString() : ""}
                </p>
              </div>
            )}
            <div className="border-2 border-[#7c3aed] px-4 py-2 text-center">
              <p className="font-display text-lg tracking-[0.2em] text-[#7c3aed]">FULFILLED</p>
              <p className="font-jmono text-[8px] uppercase tracking-[0.3em] text-[#7c3aed]">
                {doc.status === "fulfilled" ? "Commission complete" : "Provisional — pending completion"}
              </p>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
};

export default Receipt;
